import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, Shield, Clock, MapPin, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Repairs", to: "/repairs" },
  { label: "FAQ", to: "/faq" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-150 ${
        scrolled ? "header-glass border-b border-[#1F2937]" : "bg-transparent"
      }`}
    >
      {/* Emergency top bar */}
      <div className="bg-[#D95D39] text-white text-xs sm:text-sm font-bold tracking-wider text-center py-1.5 px-4">
        <span className="inline-flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          24/7 EMERGENCY WILDLIFE REMOVAL
          <span className="hidden sm:inline">—</span>
          <a href="tel:9042376253" className="underline hover:no-underline" data-testid="emergency-bar-phone">
            904-237-6253
          </a>
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img 
            src="/logo.png" 
            alt="Double D Trapping Logo" 
            className="sm:h-10 w-auto sm:h-20 object-contain" 
          />
            
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors duration-150 rounded-sm ${
                  location.pathname === link.to
                    ? "text-[#D95D39]"
                    : "text-[#9CA3AF] hover:text-[#F8FAFC]"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Call button + mobile toggle */}
          <div className="flex items-center gap-3">
            <a href="tel:9042376253" data-testid="header-call-btn">
              <Button className="bg-[#D95D39] hover:bg-[#c04d2d] text-white font-bold tracking-wider text-xs sm:text-sm rounded-sm btn-glow h-9 sm:h-10 px-3 sm:px-5">
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">CALL NOW</span>
                <span className="sm:hidden">CALL</span>
              </Button>
            </a>
            <button
              className="lg:hidden text-[#9CA3AF] hover:text-[#F8FAFC] p-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div
          className="lg:hidden border-t border-[#1F2937] bg-[#0B110E]/95 backdrop-blur-md"
          data-testid="mobile-nav"
        >
          <nav className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 text-sm font-medium tracking-wide rounded-sm transition-colors duration-150 ${
                  location.pathname === link.to
                    ? "text-[#D95D39] bg-[#1E293B]/50"
                    : "text-[#9CA3AF] hover:text-[#F8FAFC] hover:bg-[#1E293B]/30"
                }`}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#070D0A] border-t border-[#1F2937] relative" data-testid="main-footer">
    {/* Emergency CTA */}
    <div className="bg-[#D95D39] py-12 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <p className="text-[#0B110E] uppercase text-xs sm:text-sm font-bold tracking-[0.2em] mb-3">Wildlife Emergency?</p>
        <h2 className="text-white font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-4">
          Call Right Now.
        </h2>
        <a
          href="tel:9042376253"
          className="inline-flex items-center gap-3 bg-[#0B110E] text-white font-black text-xl sm:text-2xl tracking-wider px-8 py-4 rounded-sm hover:bg-[#0F1A15] transition-colors duration-150"
          data-testid="footer-call-btn"
        >
          <Phone className="w-6 h-6" />
          904-237-6253
        </a>
        <p className="text-white/70 text-sm mt-3">We Pick Up. Day or Night.</p>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8">
        {/* Company */}
        <div>
          <div className="flex items-center gap-3 mb-5">
          <img 
            src="/logo.png" 
            alt="Double D Trapping Logo" 
            className="h-14 sm:h-20 w-auto object-contain" 
          />
            <div>
              <div className="font-black text-[#F8FAFC] text-sm tracking-tight">DOUBLE D TRAPPING</div>
              <div className="text-[#64748B] text-xs">St. Augustine, FL</div>
            </div>
          </div>
          <p className="text-[#9CA3AF] text-sm leading-relaxed">
            50 years of professional wildlife removal. Licensed, insured, and available 24/7.
          </p>
        </div>

        {/* Services */}
        <div>
          <h3 className="text-[#F8FAFC] font-bold text-sm uppercase tracking-[0.15em] mb-4">Services</h3>
          <ul className="space-y-2.5">
            {["Raccoons & Opossums", "Rodents", "Wild Hogs", "Squirrels", "Bats", "Armadillos & Moles"].map((s) => (
              <li key={s}>
                <Link to="/services" className="text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150 flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3" />{s}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-[#F8FAFC] font-bold text-sm uppercase tracking-[0.15em] mb-4">Quick Links</h3>
          <ul className="space-y-2.5">
            {[
              { label: "Repairs & Exclusion", to: "/repairs" },
              { label: "Attic Sanitation", to: "/services/attic-sanitation" },
              { label: "FAQ", to: "/faq" },
              { label: "About Us", to: "/about" },
              { label: "Track Your Job", to: "/track" },
              { label: "Get a Free Quote", to: "/contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-[#9CA3AF] text-sm hover:text-[#D95D39] transition-colors duration-150 flex items-center gap-1.5">
                  <ChevronRight className="w-3 h-3" />{l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-[#F8FAFC] font-bold text-sm uppercase tracking-[0.15em] mb-4">Contact</h3>
          <div className="space-y-3">
            <a href="tel:9042376253" className="text-[#D95D39] font-bold text-lg flex items-center gap-2">
              <Phone className="w-4 h-4" /> 904-237-6253
            </a>
            <div className="flex items-start gap-2 text-[#9CA3AF] text-sm">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>St. Augustine, FL<br />St. Johns & Flagler Counties</span>
            </div>
            <div className="flex items-center gap-2 text-[#9CA3AF] text-sm">
              <Clock className="w-4 h-4 shrink-0" />
              <span>Available 24/7</span>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#D95D39]" />
            <span className="text-[#9CA3AF] text-xs">Licensed & Insured</span>
          </div>
        </div>
      </div>

      <div className="section-divider mt-12 mb-6" />
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-[#64748B] text-xs">
        <p>&copy; {new Date().getFullYear()} Double D Trapping LLC. All rights reserved.</p>
        <p>Federal Swine Dealers License #FL 04513-16</p>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#0B110E]">
      <Header />
      <main className="flex-1 pt-[calc(2.5rem+4rem)] sm:pt-[calc(2.5rem+5rem)]">
        {children}
      </main>
      <Footer />
    </div>
  );
}
